import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, IdentificationCard, FileText, Scales, Lock,
  SealCheck, Camera, Bell, CreditCard, ArrowRight, CalendarCheck,
} from '@phosphor-icons/react';
import PaymentProtection from '../components/landing/PaymentProtection';
import RealTestimonials from '../components/landing/RealTestimonials';

const VERIFICATION_CARDS = [
  {
    Icon: IdentificationCard,
    title: 'License Verified',
    body: 'Contractors upload their state license. Our team checks it against issuing-board records before the badge goes live.',
    color: '#D95800',
  },
  {
    Icon: ShieldCheck,
    title: 'Insurance on File',
    body: 'Proof of liability insurance is required for the Verified Pro badge — so a mistake on your property is never your problem.',
    color: '#2B4C6F',
  },
  {
    Icon: SealCheck,
    title: 'Verified Pro Badge',
    body: 'Only pros who clear both checks carry the badge. It appears on their public profile, quotes, and marketplace cards.',
    color: '#10B981',
  },
];

const RECORD_CARDS = [
  {
    Icon: FileText,
    title: 'E-Signed Contracts',
    body: 'Scope, price, and milestones locked in a legally signed PDF before work starts. Both parties keep a copy forever.',
  },
  {
    Icon: Camera,
    title: 'Timestamped Progress',
    body: 'Every milestone, photo, and approval is written to a tamper-evident timeline — the record that settles disagreements.',
  },
  {
    Icon: Bell,
    title: 'Approval Records',
    body: 'Payments only move when the homeowner taps Approve. Each approval is stamped with who, what, and exactly when.',
  },
  {
    Icon: CalendarCheck,
    title: 'Calendar-Backed Bookings',
    body: 'Dates are requested and accepted in-app, auto-blocked on the pro\u2019s public calendar, and exportable to Google Calendar — so scheduling disputes never start.',
  },
];

const DISPUTE_STEPS = [
  { step: '01', title: 'Funds Freeze', body: 'The moment a dispute opens, escrow locks. Nobody can move the money while it\u2019s contested.' },
  { step: '02', title: 'Both Sides Submit', body: 'Photos, contracts, timeline records, and messages are gathered — most of it already lives on the job record.' },
  { step: '03', title: 'ConstructHelp Mediates', body: 'A neutral reviewer walks both parties toward a resolution. Most disputes settle here.' },
  { step: '04', title: 'Binding Arbitration', body: 'If mediation fails, arbitration issues a final decision and escrow is released accordingly. No chargebacks, no lawyers.' },
];

const SECURITY_ROWS = [
  { Icon: CreditCard, text: 'Payments are processed by Stripe. Card numbers never touch our servers.' },
  { Icon: Lock, text: 'Escrow releases require homeowner approval — payouts are single-release and atomically guarded.' },
  { Icon: ShieldCheck, text: 'The platform undergoes recurring security audits covering payments, public links, and data exposure.' },
];

export default function TrustCenter() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#E8E9EB] dark:bg-[#121212] text-neutral-900 dark:text-white" data-testid="trust-center-page">
      {/* Hero */}
      <section className="bg-black text-white border-b-4 border-black py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="inline-flex items-center gap-2 bg-[#D95800] text-black text-[10px] font-black uppercase tracking-widest px-3 py-1 border-2 border-white mb-5 shadow-[3px_3px_0px_#fff]">
            <ShieldCheck size={14} weight="fill" /> Trust Center
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-none mb-4">
            Built so nobody
            <span className="block text-[#D95800]">gets burned.</span>
          </h1>
          <p className="text-base md:text-lg text-neutral-300 max-w-2xl">
            Escrow-protected payments, verified pros, signed contracts, and a real dispute
            process. Here is exactly how ConstructHelp keeps both sides safe — every job,
            every payment, every time.
          </p>
        </div>
      </section>

      {/* Why you can trust us — sales strip */}
      <section className="border-b-4 border-black bg-white dark:bg-[#0F172A]" data-testid="trust-promise-strip">
        <div className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              icon: Lock,
              title: 'Your money is untouchable',
              copy: 'Payments sit in secure escrow and only release when you approve the finished work. No surprises, no runaway deposits.',
              testid: 'trust-promise-escrow',
            },
            {
              icon: SealCheck,
              title: 'Every pro, actually vetted',
              copy: 'Licenses, insurance, and identity checked before anyone can take your job. If they wear our badge, they earned it.',
              testid: 'trust-promise-verified',
            },
            {
              icon: FileText,
              title: 'Everything in writing',
              copy: 'Scope, price, and timeline locked into a signed agreement before work starts — so both sides know exactly what was promised.',
              testid: 'trust-promise-contracts',
            },
            {
              icon: Scales,
              title: 'Real people settle disputes',
              copy: 'If something goes sideways, our arbitration team reviews the evidence and makes it right. Humans, not bots.',
              testid: 'trust-promise-disputes',
            },
          ].map(({ icon: Icon, title, copy, testid }) => (
            <div key={title} className="border-4 border-black bg-[#E8E9EB] dark:bg-[#121212] p-5 shadow-[5px_5px_0px_#000]" data-testid={testid}>
              <Icon size={28} weight="fill" className="text-[#D95800] mb-3" />
              <div className="text-base font-black uppercase tracking-tight leading-tight mb-1.5">{title}</div>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-snug">{copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Escrow explainer (reused landing section) */}
      <PaymentProtection />

      {/* Verification */}
      <section className="py-16 border-b-4 border-black" data-testid="trust-verification-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-10">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#D95800] mb-2">Who you're hiring</div>
            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight">Verified means verified.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {VERIFICATION_CARDS.map(({ Icon, title, body, color }) => (
              <div
                key={title}
                className="bg-white dark:bg-[#0F172A] border-4 border-black p-5 shadow-[6px_6px_0px_#000] hover:shadow-[8px_8px_0px_#000] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all"
                style={{ borderTopColor: color, borderTopWidth: 8 }}
              >
                <div className="w-12 h-12 flex items-center justify-center border-2 border-black mb-3" style={{ backgroundColor: color }}>
                  <Icon size={24} weight="fill" className="text-white" />
                </div>
                <div className="font-black uppercase text-sm tracking-wide mb-1">{title}</div>
                <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Paper trail */}
      <section className="py-16 border-b-4 border-black bg-white dark:bg-[#0F172A]" data-testid="trust-records-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-10">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#D95800] mb-2">Everything on record</div>
            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight">A paper trail that protects you.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {RECORD_CARDS.map(({ Icon, title, body }) => (
              <div key={title} className="border-4 border-black bg-[#E8E9EB] dark:bg-[#121212] p-5 shadow-[6px_6px_0px_#000]">
                <div className="w-12 h-12 bg-black flex items-center justify-center border-2 border-black mb-3">
                  <Icon size={24} weight="fill" className="text-[#D95800]" />
                </div>
                <div className="font-black uppercase text-sm tracking-wide mb-1">{title}</div>
                <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dispute process */}
      <section className="py-16 border-b-4 border-black" data-testid="trust-dispute-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 bg-amber-400 text-black text-[10px] font-black uppercase tracking-widest px-3 py-1 border-2 border-black mb-3 shadow-[3px_3px_0px_#000]">
              <Scales size={14} weight="fill" /> If things go wrong
            </div>
            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight">The dispute process, step by step.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DISPUTE_STEPS.map(({ step, title, body }) => (
              <div key={step} className="bg-white dark:bg-[#0F172A] border-4 border-black p-5 shadow-[6px_6px_0px_#000]">
                <div className="text-4xl font-black text-neutral-200 dark:text-neutral-700 mb-2">{step}</div>
                <div className="font-black uppercase text-sm tracking-wide mb-1">{title}</div>
                <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="py-16 border-b-4 border-black bg-black text-white" data-testid="trust-security-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-8">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#D95800] mb-2">Under the hood</div>
            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight">Payments & data security.</h2>
          </div>
          <div className="space-y-4">
            {SECURITY_ROWS.map(({ Icon, text }) => (
              <div key={text} className="flex items-start gap-4 border-2 border-white/20 bg-white/5 p-4">
                <div className="w-10 h-10 bg-[#D95800] border-2 border-white flex items-center justify-center flex-shrink-0">
                  <Icon size={20} weight="fill" className="text-black" />
                </div>
                <p className="text-sm md:text-base text-neutral-200 leading-relaxed pt-2">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real testimonials */}
      <RealTestimonials heading="Real reviews from real jobs" />

      {/* CTA */}
      <section className="py-16" data-testid="trust-cta-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-[#D95800] border-4 border-black p-8 md:p-10 shadow-[8px_8px_0px_#000] flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-black leading-tight">
                Hire — or get hired — without the gamble.
              </h2>
              <p className="text-sm md:text-base text-black/80 mt-2 font-medium">
                0% commission. Escrow on every dollar. Verified pros only.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/get-quotes"
                data-testid="trust-cta-homeowner"
                className="inline-flex items-center justify-center gap-2 bg-black text-white font-black uppercase text-sm tracking-wide px-6 py-3.5 border-2 border-black shadow-[4px_4px_0px_#fff] hover:-translate-y-0.5 transition-transform"
              >
                Get Quotes <ArrowRight size={16} weight="bold" />
              </Link>
              <Link
                to="/register"
                data-testid="trust-cta-pro"
                className="inline-flex items-center justify-center gap-2 bg-white text-black font-black uppercase text-sm tracking-wide px-6 py-3.5 border-2 border-black shadow-[4px_4px_0px_#000] hover:-translate-y-0.5 transition-transform"
              >
                Join as a Pro
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
